/**
 * OrivonAI Data Models — Nova Proactive Intelligence
 *
 * Schema definitions for journey_timeline, logs, fired_triggers,
 * loss_history, and user extensions.
 *
 * PRIVACY: loss_history is stored separately and MUST be encrypted
 * with a separate key. Never include in analytics, admin dashboards,
 * or logs. baby_name must NEVER appear in push notification payload text.
 */

// ── Log types (enum) ─────────────────────────────────────────
export const LOG_TYPES = [
  'mood',
  'symptom',
  'cycle',
  'test_result',
  'medication',
  'breastfeeding',
  'hot_flash',
  'pregnancy_test',
];

// ── Loss types (enum) ────────────────────────────────────────
export const LOSS_TYPES = [
  'early_miscarriage',
  'late_miscarriage',
  'stillbirth',
  'ectopic',
  'tfmr',
  'ivf_loss',
  'recurrent',
];

// ── Journey timeline model (per user) ─────────────────────────
/**
 * @typedef {Object} JourneyTimeline
 * @property {string} userId
 * @property {string} chapter - cycle | ivf | ttc | pregnancy | postpartum | menopause | loss
 * @property {string} [chapter_start_date] - ISO date YYYY-MM-DD
 * @property {Object} [key_dates]
 * @property {string} [key_dates.due_date]
 * @property {string} [key_dates.retrieval_date]
 * @property {string} [key_dates.beta_date]
 * @property {string[]} [key_dates.loss_dates]
 * @property {string} [key_dates.transfer_date]
 * @property {string} [key_dates.treatment_start]
 * @property {Array<{name?: string, dob?: string}>} [children]
 * @property {Object} [cycle_data]
 * @property {string} [cycle_data.last_period_date]
 * @property {number} [cycle_data.avg_cycle_length]
 * @property {boolean} [cycle_data.is_irregular]
 * @property {number} [cycle_data.months_trying] - TTC: months trying to conceive
 * @property {string} [cycle_data.tracking_method] - OPKs, BBT, app, etc.
 * @property {Object} [ivf_protocol]
 * @property {number} [ivf_protocol.cycle_number] - which IVF cycle (1, 2, 3...)
 * @property {number} [ivf_protocol.cycle_day] - stim days
 * @property {number} [ivf_protocol.follicles] - from last scan
 * @property {number} [ivf_protocol.eggs_retrieved]
 * @property {number} [ivf_protocol.fertilized]
 * @property {number} [ivf_protocol.embryos]
 * @property {number} [ivf_protocol.transfers] - total transfers so far
 * @property {string} [ivf_protocol.protocol_type] - antagonist, long lupron, etc.
 * @property {string} [ivf_protocol.clinic_name]
 * @property {string} [ivf_protocol.retrieval_date]
 * @property {string} [ivf_protocol.transfer_date]
 * @property {string} [ivf_protocol.diagnosis] - PCOS, DOR, unexplained, etc.
 * @property {Object} [flags]
 * @property {boolean} [flags.is_anonymous]
 * @property {boolean} [flags.is_under_18]
 * @property {boolean} [flags.has_had_loss]
 * @property {string[]} [flags.loss_dates]
 * @property {Array} [loss_history] - ENCRYPTED SEPARATELY. See LossHistoryEntry.
 * @property {string} [updated_at]
 */

export function createJourneyTimeline(userId, overrides = {}) {
  return {
    userId: String(userId || ''),
    chapter: overrides.chapter || 'cycle',
    chapter_start_date: overrides.chapter_start_date || null,
    key_dates: overrides.key_dates || {},
    children: overrides.children || [],
    cycle_data: overrides.cycle_data || {},
    ivf_protocol: overrides.ivf_protocol || {},
    flags: overrides.flags || {},
    updated_at: new Date().toISOString(),
    ...overrides,
  };
}

// ── Loss history entry (stored encrypted, separate key) ───────
/**
 * @typedef {Object} LossHistoryEntry
 * @property {string} loss_type - early_miscarriage | late_miscarriage | stillbirth | ectopic | tfmr | ivf_loss | recurrent
 * @property {string} loss_date - YYYY-MM-DD
 * @property {string} [due_date] - original due date
 * @property {number} [gestational_age_weeks]
 * @property {string} [baby_name] - HIGHEST PRIVACY. Never in push payloads.
 * @property {boolean} [had_procedure]
 * @property {string|null} [procedure_date]
 * @property {boolean} [was_ivf]
 */
export function createLossHistoryEntry(entry) {
  return {
    loss_type: LOSS_TYPES.includes(entry.loss_type) ? entry.loss_type : 'early_miscarriage',
    loss_date: entry.loss_date || new Date().toISOString().slice(0, 10),
    due_date: entry.due_date || null,
    gestational_age_weeks: entry.gestational_age_weeks ?? null,
    baby_name: entry.baby_name || undefined,
    had_procedure: Boolean(entry.had_procedure),
    procedure_date: entry.procedure_date || null,
    was_ivf: Boolean(entry.was_ivf),
  };
}

// ── Log entry ────────────────────────────────────────────────
/**
 * @typedef {Object} LogEntry
 * @property {string} id
 * @property {string} user_id
 * @property {string} log_type - mood | symptom | cycle | test_result | medication | breastfeeding | hot_flash | pregnancy_test
 * @property {string} logged_at - ISO datetime
 * @property {Object} data - flexible schema per log_type
 */
export function createLogEntry(userId, logType, data = {}) {
  const id = `log_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
  return {
    id,
    user_id: String(userId || ''),
    log_type: LOG_TYPES.includes(logType) ? logType : 'mood',
    logged_at: new Date().toISOString(),
    data: typeof data === 'object' ? data : {},
  };
}

// ── Fired trigger entry ──────────────────────────────────────
/**
 * @typedef {Object} FiredTriggerEntry
 * @property {string} user_id
 * @property {string} message_id
 * @property {string} fired_at - ISO datetime
 */
export function createFiredTriggerEntry(userId, messageId) {
  return {
    user_id: String(userId || ''),
    message_id: String(messageId || ''),
    fired_at: new Date().toISOString(),
  };
}

// ── Nova notification entry (in-app proactive message) ─────────
/**
 * @typedef {Object} NovaNotificationEntry
 * @property {string} id
 * @property {string} user_id
 * @property {string} message_id
 * @property {string} message - resolved text for display
 * @property {string} created_at - ISO datetime
 * @property {boolean} read
 */
export function createNovaNotificationEntry(userId, messageId, message) {
  const id = `nova_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
  return {
    id,
    user_id: String(userId || ''),
    message_id: String(messageId || ''),
    message: String(message || ''),
    created_at: new Date().toISOString(),
    read: false,
  };
}

// ── User model extensions ────────────────────────────────────
/**
 * Add to user record:
 * - subsequent_pregnancy_after_loss: boolean
 * - recurrent_loss_count: number (default 0)
 * - last_login_date: ISO date string (updated on session)
 */
export const USER_EXTENSION_DEFAULTS = {
  subsequent_pregnancy_after_loss: false,
  recurrent_loss_count: 0,
  last_login_date: null,
};
